import { useCallback, useEffect, useRef, useState } from 'react';

import { type AlignerDebugSnapshot, ALIGNER_CONSTANTS } from '@/lib/speech/fuzzy-aligner';
import type { SpeechError, TranscriptResult } from '@/lib/speech/speech-recognition-adapter';

import type { SpeechAlignmentDebugRefs } from './use-speech-alignment';

export interface DebugLogEntry {
  ts: number;
  relativeMs: number;
  type: 'interim' | 'final' | 'error' | 'end' | 'cursor-override' | 'snapshot';
  data: unknown;
}

export interface RehearsalDebugData {
  // Recognition
  interimText: string;
  finalText: string;
  isListening: boolean;
  errors: SpeechError[];
  restartCount: number;
  lastEventMs: number | null;

  // Aligner snapshot
  aligner: AlignerDebugSnapshot | null;

  // Debug actions
  setCursorPosition: (position: number) => void;
  downloadLog: () => void;
}

const POLL_INTERVAL = 200;
const GAP_THRESHOLD_MS = 2000;

export function useRehearsalDebug(
  debugRefs: SpeechAlignmentDebugRefs,
  enabled: boolean,
): RehearsalDebugData | null {
  const [interimText, setInterimText] = useState('');
  const [finalText, setFinalText] = useState('');
  const [errors, setErrors] = useState<SpeechError[]>([]);
  const [restartCount, setRestartCount] = useState(0);
  const [lastEventMs, setLastEventMs] = useState<number | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [alignerSnapshot, setAlignerSnapshot] = useState<AlignerDebugSnapshot | null>(null);

  // Track whether we're intentionally stopping (vs auto-restart)
  const stoppedIntentionally = useRef(false);

  // Event log (ref to avoid re-renders on every event)
  const eventLogRef = useRef<DebugLogEntry[]>([]);
  const sessionStartMsRef = useRef<number | null>(null);

  const pushLogEntry = useCallback((type: DebugLogEntry['type'], data: unknown) => {
    const now = Date.now();
    if (sessionStartMsRef.current === null) {
      sessionStartMsRef.current = now;
    }
    eventLogRef.current.push({
      ts: now,
      relativeMs: now - sessionStartMsRef.current,
      type,
      data,
    });
  }, []);

  // Subscribe to adapter events
  useEffect(() => {
    if (!enabled) return;
    const adapter = debugRefs.adapterRef.current;
    if (!adapter) return;

    const unsubInterim = adapter.onInterim((words: string[], meta?: TranscriptResult) => {
      setInterimText(words.join(' '));
      setLastEventMs(Date.now());
      pushLogEntry('interim', {
        words,
        confidence: meta?.confidence ?? null,
        alternativeCount: meta?.alternativeCount ?? 0,
      });
    });

    const unsubFinal = adapter.onTranscript((words: string[], meta?: TranscriptResult) => {
      setFinalText(words.join(' '));
      setLastEventMs(Date.now());
      pushLogEntry('final', {
        words,
        confidence: meta?.confidence ?? null,
        alternativeCount: meta?.alternativeCount ?? 0,
      });
    });

    const unsubError = adapter.onError((error) => {
      setErrors((prev) => [...prev.slice(-19), error]);
      setLastEventMs(Date.now());
      pushLogEntry('error', { type: error.type, message: error.message });
    });

    const unsubEnd = adapter.onEnd(() => {
      if (!stoppedIntentionally.current) {
        setRestartCount((c) => c + 1);
      }
      stoppedIntentionally.current = false;
      pushLogEntry('end', { restartCount: restartCount + 1 });
    });

    return () => {
      unsubInterim();
      unsubFinal();
      unsubError();
      unsubEnd();
    };
  }, [enabled, debugRefs, pushLogEntry, restartCount]);

  // Poll aligner snapshot and listening state
  useEffect(() => {
    if (!enabled) return;

    const id = setInterval(() => {
      const aligner = debugRefs.alignerRef.current;
      if (aligner) {
        const snapshot = aligner.getDebugSnapshot();
        setAlignerSnapshot(snapshot);
        pushLogEntry('snapshot', snapshot);
      }
      const adapter = debugRefs.adapterRef.current;
      if (adapter) {
        setIsListening(adapter.isListening);
      }
    }, POLL_INTERVAL);

    return () => clearInterval(id);
  }, [enabled, debugRefs, pushLogEntry]);

  const setCursorPosition = useCallback(
    (position: number) => {
      const aligner = debugRefs.alignerRef.current;
      if (!aligner) return;
      const oldPosition = aligner.cursorPosition;
      debugRefs.setCursorPosition(position);
      pushLogEntry('cursor-override', { oldPosition, newPosition: position });
    },
    [debugRefs, pushLogEntry],
  );

  /* v8 ignore start -- tested via Chrome MCP integration; blob/download APIs not available in jsdom */
  const downloadLog = useCallback(() => {
    const aligner = debugRefs.alignerRef.current;
    const detector = debugRefs.detectorRef.current;
    const log = eventLogRef.current;

    const finalEvents = log.filter((e) => e.type === 'final');
    const errorEvents = log.filter((e) => e.type === 'error');
    const cursorOverrides = log.filter((e) => e.type === 'cursor-override');

    // Compute speech recognition gaps >2s from event timestamps
    const speechEvents = log.filter((e) => e.type === 'interim' || e.type === 'final');
    const gaps: Array<{ startMs: number; endMs: number; durationMs: number }> = [];
    for (let i = 1; i < speechEvents.length; i++) {
      const duration = speechEvents[i].ts - speechEvents[i - 1].ts;
      if (duration >= GAP_THRESHOLD_MS) {
        gaps.push({
          startMs: speechEvents[i - 1].relativeMs,
          endMs: speechEvents[i].relativeMs,
          durationMs: duration,
        });
      }
    }

    const exportData = {
      metadata: {
        downloadedAt: new Date().toISOString(),
        sessionStartedAt: sessionStartMsRef.current
          ? new Date(sessionStartMsRef.current).toISOString()
          : null,
        browser: navigator.userAgent,
        scriptWordCount: aligner?.getDebugSnapshot().scriptWordCount ?? 0,
        sectionCount: detector?.sectionTimings.length ?? 0,
        alignerConstants: ALIGNER_CONSTANTS,
      },
      accuracy: {
        finalCursor: aligner?.cursorPosition ?? 0,
        scriptWordCount: aligner?.getDebugSnapshot().scriptWordCount ?? 0,
      },
      session: {
        durationMs: detector?.totalDurationMs ?? 0,
        finalResultCount: finalEvents.length,
        restartCount,
        errorCount: errorEvents.length,
      },
      aligner: aligner?.getDebugSnapshot() ?? null,
      scriptWords: debugRefs.scriptWordsRef.current,
      sectionBoundaries: debugRefs.sectionBoundariesRef.current,
      sectionTimings: detector?.sectionTimings ?? [],
      gaps,
      errors: errorEvents,
      transcripts: finalEvents,
      cursorOverrides,
      eventLog: log,
    };

    const json = JSON.stringify(exportData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    a.href = url;
    a.download = `rehearsal-debug-${timestamp}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [debugRefs, restartCount]); /* v8 ignore stop */

  if (!enabled) return null;

  return {
    interimText,
    finalText,
    isListening,
    errors,
    restartCount,
    lastEventMs,
    aligner: alignerSnapshot,
    setCursorPosition,
    downloadLog,
  };
}
